/**
 * Project Form Page
 *
 * Form for creating and editing projects
 * Supports both create and edit modes based on route params
 *
 * @module pages/projects/ProjectForm
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import {
  createProject,
  updateProject,
  getProject,
  deleteProject,
} from '../../services/projectService';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { listClients } from '../../services/clientService';
import { getProjectTypes } from '../../services/projectTypeService';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';

/**
 * Form validation schema
 */
const projectSchema = yup.object().shape({
  title: yup
    .string()
    .required('El título es requerido')
    .min(3, 'El título debe tener al menos 3 caracteres')
    .max(200, 'El título no puede exceder 200 caracteres'),
  description: yup
    .string()
    .max(1000, 'La descripción no puede exceder 1000 caracteres'),
  client_id: yup
    .string()
    .nullable(),
  project_type_id: yup
    .string()
    .nullable(),
  estimated_payment_date: yup
    .date()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    }),
  quoted_amount: yup
    .number()
    .nullable()
    .min(0, 'El presupuesto no puede ser negativo')
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    }),
});

/**
 * ProjectForm component
 */
const ProjectForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const clientIdParam = searchParams.get('client');
  const { isAdmin, isSupervisor, isTechnician } = useAuth();
  const isEditMode = !!projectId;
  const canEditBudget = isAdmin || isSupervisor;

  const [loading, setLoading] = useState(false);
  const [fetchingProject, setFetchingProject] = useState(isEditMode);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);

  // #748: ProjectForm es el otro camino de alta de obra del carril web (el
  // primero es QuotationBuilder). Sin este selector, generate_project_number
  // cae a 'XX' en el segmento de tipo — ver la nota en projectSchema arriba.
  const { data: projectTypes = [] } = useQuery({
    queryKey: ['project_types'],
    queryFn: () => getProjectTypes(),
    staleTime: 1000 * 60 * 10,
  });
  const [error, setFormError] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // React Hook Form
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(projectSchema),
    mode: 'onTouched',
    defaultValues: {
      title: '',
      description: '',
      client_id: '',
      project_type_id: '',
      estimated_payment_date: '',
      quoted_amount: '',
    },
  });

  /**
   * Check permissions
   */
  useEffect(() => {
    if (!isAdmin && !isTechnician) {
      toast.error('No tienes permisos para crear o editar proyectos');
      navigate('/proyectos');
    }
  }, [isAdmin, isTechnician, navigate]);

  /**
   * Fetch clients for dropdown
   */
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const { data, error } = await listClients({ pageSize: 200 });

        if (error) throw error;

        setClients(data || []);
      } catch (error) {
        console.error('Fetch clients error:', error);
        toast.error('Error al cargar clientes');
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, []);

  /**
   * Pre-fill client from query param
   */
  useEffect(() => {
    if (clientIdParam && !isEditMode) {
      setValue('client_id', clientIdParam);
    }
  }, [clientIdParam, isEditMode, setValue]);

  /**
   * Fetch project data if editing
   */
  useEffect(() => {
    if (!isEditMode) return;

    const fetchProjectData = async () => {
      try {
        setFetchingProject(true);
        const { data, error } = await getProject(projectId);

        if (error) throw error;

        // Populate form with project data
        reset({
          title: data.title || '',
          description: data.description || '',
          client_id: data.client_id || '',
          project_type_id: data.project_type_id || '',
          estimated_payment_date: data.estimated_payment_date
            ? data.estimated_payment_date.split('T')[0]
            : '',
          quoted_amount: data.quoted_amount ?? '',
        });
      } catch (error) {
        console.error('Fetch project error:', error);
        setFormError(error.message);
        toast.error('Error al cargar el proyecto');
      } finally {
        setFetchingProject(false);
      }
    };

    fetchProjectData();
  }, [projectId, isEditMode, reset]);

  /**
   * Handle form submission
   */
  const onSubmit = async (formData) => {
    // Guard: catch blank title before the async save so feedback is immediate
    if (!formData.title || !formData.title.trim()) {
      setError('title', { type: 'manual', message: 'El título es requerido' });
      return;
    }

    try {
      setLoading(true);
      setFormError(null);

      // Clean up empty values
      const cleanData = {
        ...formData,
        description: formData.description?.trim() || null,
        client_id: formData.client_id || null,
        project_type_id: formData.project_type_id || null,
        estimated_payment_date: formData.estimated_payment_date || null,
        quoted_amount: formData.quoted_amount === '' ? null : formData.quoted_amount,
      };

      // Budget is financial data — never submit it from a role that can't see the field.
      if (!canEditBudget) {
        delete cleanData.quoted_amount;
      }

      let result;
      if (isEditMode) {
        result = await updateProject(projectId, cleanData);
      } else {
        result = await createProject(cleanData);
      }

      if (result.error) throw result.error;

      toast.success(
        isEditMode
          ? 'Proyecto actualizado exitosamente'
          : 'Proyecto creado exitosamente'
      );

      queryClient.invalidateQueries(['projects']);
      if (isEditMode) {
        queryClient.invalidateQueries(['project', result.data.id]);
      }

      // Navigate to project detail page
      navigate(`/proyectos/${result.data.id}`);
    } catch (error) {
      console.error('Submit error:', error);
      setFormError(error.message);
      toast.error(
        isEditMode
          ? 'Error al actualizar el proyecto'
          : 'Error al crear el proyecto'
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    if (isEditMode) {
      navigate(`/proyectos/${projectId}`);
    } else {
      navigate('/proyectos');
    }
  };

  /**
   * Handle delete project (Admin only)
   */
  const handleDeleteProject = async () => {
    try {
      setDeleteLoading(true);
      const result = await deleteProject(projectId);

      if (result.error) throw result.error;

      toast.success('Proyecto eliminado exitosamente');
      queryClient.invalidateQueries(['projects']);
      queryClient.removeQueries(['project', projectId]);
      navigate('/proyectos');
    } catch (error) {
      console.error('Delete project error:', error);
      // El mensaje del servicio distingue "falló la query" de "RLS no borró
      // nada"; tragárselo detrás de un texto genérico fue parte de por qué el
      // borrado silencioso pasó desapercibido.
      toast.error(error?.message || 'Error al eliminar el proyecto');
      setDeleteLoading(false);
    }
  };

  // Loading state while fetching project
  if (fetchingProject) {
    return (
      <DashboardLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando proyecto...</span>
          </Spinner>
        </Container>
      </DashboardLayout>
    );
  }

  // Error state
  if (error && isEditMode) {
    return (
      <DashboardLayout>
        <Container className="mt-4">
          <Alert variant="danger">
            <Alert.Heading>Error al cargar el proyecto</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" onClick={() => navigate('/proyectos')}>
              Volver a Proyectos
            </Button>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col lg={8}>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">
                <i className={`bi ${isEditMode ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
                {isEditMode ? 'Editar Proyecto' : 'Nuevo Proyecto'}
              </h4>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setFormError(null)}>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit(onSubmit)}>
                {/* Title */}
                <Form.Group className="mb-3" controlId="title">
                  <Form.Label>
                    Nombre del Proyecto <span className="text-danger">*</span>
                  </Form.Label>
                  <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="text"
                        placeholder="Ej: Mantenimiento de Equipos Médicos - Hospital Central"
                        isInvalid={!!errors.title}
                        disabled={isSubmitting}
                        data-testid="project-form-title-input"
                      />
                    )}
                  />
                  {errors.title && (
                    <Form.Control.Feedback type="invalid">
                      {errors.title.message}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    Ponle el nombre con el que quieras identificar este proyecto. Es lo que se mostrará en grande.
                  </Form.Text>
                </Form.Group>

                {/* Description */}
                <Form.Group className="mb-3" controlId="description">
                  <Form.Label>Descripción</Form.Label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        as="textarea"
                        rows={4}
                        placeholder="Descripción detallada del proyecto..."
                        isInvalid={!!errors.description}
                        disabled={isSubmitting}
                        data-testid="project-form-description-textarea"
                      />
                    )}
                  />
                  {errors.description && (
                    <Form.Control.Feedback type="invalid">
                      {errors.description.message}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    Opcional. Máximo 1000 caracteres.
                  </Form.Text>
                </Form.Group>

                {/* Client */}
                <Form.Group className="mb-3" controlId="client_id">
                  <Form.Label>Cliente</Form.Label>
                  <Controller
                    name="client_id"
                    control={control}
                    render={({ field }) => (
                      <Form.Select
                        {...field}
                        isInvalid={!!errors.client_id}
                        disabled={isSubmitting || loadingClients}
                        data-testid="project-form-client-select"
                      >
                        <option value="">Seleccionar cliente...</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.company_name || client.primary_contact_name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  />
                  {errors.client_id && (
                    <Form.Control.Feedback type="invalid">
                      {errors.client_id.message}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    Opcional. Selecciona el cliente asociado al proyecto.
                  </Form.Text>
                </Form.Group>

                {/* Project Type — #748: sin este selector, generate_project_number
                    cae al segmento 'XX' porque nunca recibe project_type_id.
                    Mismo catálogo y mismo patrón que QuotationBuilder. */}
                <Form.Group className="mb-3" controlId="project_type_id">
                  <Form.Label>Tipo de Proyecto</Form.Label>
                  <Controller
                    name="project_type_id"
                    control={control}
                    render={({ field }) => (
                      <Form.Select
                        {...field}
                        isInvalid={!!errors.project_type_id}
                        disabled={isSubmitting}
                        data-testid="project-form-type-select"
                      >
                        <option value="">Seleccionar tipo...</option>
                        {projectTypes.map((type) => (
                          <option key={type.id || type.code} value={type.id || type.code}>
                            {type.code} — {type.label}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  />
                  {errors.project_type_id && (
                    <Form.Control.Feedback type="invalid">
                      {errors.project_type_id.message}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    Opcional. Clasifica el trabajo y aparece en el código corto del proyecto.
                  </Form.Text>
                </Form.Group>

                {/* Estimated Payment Date — #467: only shown when editing. At
                    creation time this date isn't known yet; asking for it up
                    front forced Jorge to invent a value. It's captured later,
                    once there's a real answer (edit here, or set it manually
                    after the quotation is approved). */}
                {isEditMode && (
                  <Form.Group className="mb-4" controlId="estimated_payment_date">
                    <Form.Label>Fecha Estimada de Pago</Form.Label>
                    <Controller
                      name="estimated_payment_date"
                      control={control}
                      render={({ field }) => (
                        <Form.Control
                          {...field}
                          type="date"
                          isInvalid={!!errors.estimated_payment_date}
                          disabled={isSubmitting}
                        />
                      )}
                    />
                    {errors.estimated_payment_date && (
                      <Form.Control.Feedback type="invalid">
                        {errors.estimated_payment_date.message}
                      </Form.Control.Feedback>
                    )}
                    <Form.Text className="text-muted">
                      Opcional. Fecha estimada de recepción del pago.
                    </Form.Text>
                  </Form.Group>
                )}

                {/* Budget (quoted_amount) - Admin/Supervisor Only. #467: only
                    shown when editing — at creation time there is no quotation
                    yet, so no real amount to enter.

                    #727: el texto de ayuda de abajo prometía que aprobar una
                    cotización sobrescribe este monto, y desde #639 la base hace
                    exactamente lo contrario: el trigger
                    `update_project_quoted_amount()` escribe
                    `WHERE ... AND quoted_amount IS NULL`, así que el primero
                    que llega gana y aprobar después no mueve nada — sin error,
                    porque afectar cero filas es una salida normal del trigger.
                    Medido punta a punta: 50.000 a mano + cotización aprobada de
                    80.000 → sigue en 50.000, y el presupuesto de la obra pasa a
                    medirse contra los 50.000 (alertas de 80%/100% adelantadas).
                    Acá la lógica está bien y el que mentía era el cartel. */}
                {isEditMode && canEditBudget && (
                  <Form.Group className="mb-4" controlId="quoted_amount">
                    <Form.Label>Presupuesto Asignado</Form.Label>
                    <Controller
                      name="quoted_amount"
                      control={control}
                      render={({ field }) => (
                        <Form.Control
                          {...field}
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          isInvalid={!!errors.quoted_amount}
                          disabled={isSubmitting}
                          data-testid="project-form-quoted-amount-input"
                        />
                      )}
                    />
                    {errors.quoted_amount && (
                      <Form.Control.Feedback type="invalid">
                        {errors.quoted_amount.message}
                      </Form.Control.Feedback>
                    )}
                    <Form.Text className="text-muted">
                      Opcional. Se puede asignar directamente sin esperar una cotización aceptada. Este monto queda como la línea base de la obra: aprobar una cotización más adelante NO lo reemplaza (#639). Si querés que el monto sea el de la cotización, dejá este campo vacío hasta aprobarla, o editalo a mano después.
                    </Form.Text>
                  </Form.Group>
                )}

                {/* Action Buttons */}
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    data-testid="project-form-cancel-button"
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting || loading}
                    data-testid="project-form-submit-button"
                  >
                    {isSubmitting || loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {isEditMode ? 'Actualizando...' : 'Creando...'}
                      </>
                    ) : (
                      <>
                        <i className={`bi ${isEditMode ? 'bi-check-lg' : 'bi-plus-lg'} me-2`}></i>
                        {isEditMode ? 'Actualizar Proyecto' : 'Crear Proyecto'}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Help Text */}
          {!isEditMode && (
            <Alert variant="info" className="mt-3">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Nota:</strong> El nombre que elijas es lo que se mostrará en grande. Además el sistema le asigna un código corto automático, que queda en pequeño al lado y no reemplaza tu nombre. El presupuesto y la fecha de pago no se piden acá — el presupuesto lo fija la PRIMERA cotización que se apruebe (las siguientes ya no lo cambian, #639), y ambos se pueden editar después desde el proyecto.
            </Alert>
          )}

          {/* Danger Zone - Only in Edit Mode and Admin Only */}
          {isEditMode && isAdmin && (
            <Card border="danger" className="mt-4">
              <Card.Header className="bg-danger text-white">
                <h5 className="mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Zona de Peligro
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Eliminar Proyecto</strong>
                    <p className="text-muted mb-0 small">
                      Esta acción no se puede deshacer. Se eliminarán todos los datos asociados.
                    </p>
                  </div>
                  <Button
                    variant="outline-danger"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={deleteLoading}
                    data-testid="project-form-delete-button"
                  >
                    <i className="bi bi-trash me-1"></i>
                    Eliminar Proyecto
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Delete Project Dialog */}
      <ConfirmDialog
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteProject}
        title="Eliminar Proyecto"
        message="Esta acción no se puede deshacer. Se eliminarán todos los datos asociados incluyendo cotizaciones, gastos y fotos de progreso."
        variant="danger"
        confirmText="Eliminar Proyecto"
        cancelText="Cancelar"
        requireTyping
        confirmationWord="ELIMINAR"
      />
      </Container>
    </DashboardLayout>
  );
};

export default ProjectForm;
